import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';

export default function Questionnaire() {
  const { token } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuestionnaire();
  }, []);

  const fetchQuestionnaire = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/questionnaire`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error('Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = (questionId, value) => {
    setResponses({ ...responses, [questionId]: value });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/questionnaire/submit`,
        { responses },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('TRUST-AI score calculated!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading questionnaire...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return <div className="min-h-screen flex items-center justify-center">No questions available</div>;
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const canProceed = responses[question.id] !== undefined;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" data-testid="questionnaire-page">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">TRUST-AI Assessment</CardTitle>
                    <CardDescription>Question {currentQuestion + 1} of {questions.length}</CardDescription>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-indigo-600">{Math.round(progress)}%</p>
                  <p className="text-xs text-gray-500">Complete</p>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Progress value={progress} className="mt-4 h-2" />
        </div>

        {/* Question Card */}
        <div className="max-w-3xl mx-auto">
          <Card className="border-0 shadow-2xl">
            <CardHeader className="pb-4">
              <div className="inline-block px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium mb-4 w-fit">
                {question.category.toUpperCase()}
              </div>
              <CardTitle className="text-xl" data-testid="question-text">{question.question}</CardTitle>
            </CardHeader>

            <CardContent className="pt-0">
              {question.type === 'scale' && question.options ? (
                <RadioGroup
                  value={responses[question.id]?.toString()}
                  onValueChange={(value) => handleResponse(question.id, parseInt(value))}
                  className="space-y-3"
                >
                  {question.options.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center space-x-3 p-4 rounded-lg border-2 hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer"
                      onClick={() => handleResponse(question.id, option.value)}
                      data-testid={`option-${option.value}`}
                    >
                      <RadioGroupItem value={option.value.toString()} id={`option-${option.value}`} />
                      <Label
                        htmlFor={`option-${option.value}`}
                        className="flex-1 cursor-pointer font-medium"
                      >
                        {option.label}
                      </Label>
                      <span className="text-sm text-gray-500">{option.value}/5</span>
                    </div>
                  ))}
                </RadioGroup>
              ) : question.type === 'number' ? (
                <div className="space-y-3">
                  <Label htmlFor="number-input" className="text-base">
                    Enter a number between {question.min_value || 0} and {question.max_value || 100}
                  </Label>
                  <Input
                    id="number-input"
                    type="number"
                    min={question.min_value || 0}
                    max={question.max_value || 100}
                    value={responses[question.id] || ''}
                    onChange={(e) => handleResponse(question.id, parseInt(e.target.value) || 0)}
                    className="text-lg p-6"
                    placeholder="Enter number"
                    data-testid="number-input"
                  />
                </div>
              ) : null}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  data-testid="previous-button"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>

                {isLastQuestion ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canProceed || submitting}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="submit-button"
                  >
                    {submitting ? (
                      'Calculating...'
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Calculate Score
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="next-button"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {questions.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentQuestion
                    ? 'bg-indigo-600 w-8'
                    : responses[questions[index].id] !== undefined
                    ? 'bg-green-500'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}